/**
 * Audit log: who changed what in the business — above all, who changed whose access, with the
 * roles before and after (PRD PRT-020). Served by Identity (`audit:read`: Owner and Business
 * Administrator); the portal only presents it.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronDown, ScrollText } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { useAccessResources } from "@/components/team/AccessEditor";
import { shortName } from "@/components/team/roles";
import { RolePill } from "@/components/team/ui";
import { Badge, Button, cx, EmptyState, ErrorState, FullPageSpinner, Select } from "@/components/ui";
import { restrictionSummary, type ResourceNames } from "@/lib/access";
import { identity, portal } from "@/lib/api";
import {
  AUDIT_CATEGORIES,
  describeEvent,
  roleDiff,
  type AuditCategory,
  type DescribedEvent,
  type NameOf,
} from "@/lib/audit";
import { formatDateTime, timeAgo } from "@/lib/format";
import type { AccessSnapshotRole, AuditEvent, CustomerAuditEvent } from "@/lib/types";

const panel = "rounded-[14px] border border-border bg-surface";

export function AuditLogPage() {
  const { can } = useOrganization();
  if (!can("audit:read")) {
    return (
      <EmptyState
        icon={<ScrollText className="size-5" aria-hidden />}
        title="You can't see the audit log"
        description="Only the Owner and Business Administrators can see who changed what in this business."
      />
    );
  }
  return <AuditLog />;
}

function AuditLog() {
  const me = useUser();
  const { orgId, current } = useOrganization();
  const [category, setCategory] = useState<AuditCategory>("access");
  const [actor, setActor] = useState("");
  const action = AUDIT_CATEGORIES.find((c) => c.id === category)?.action;

  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  const events = useInfiniteQuery({
    queryKey: ["audit-events", orgId, action ?? "", actor],
    queryFn: ({ pageParam }) =>
      identity.auditEvents(orgId, {
        action,
        actor_user_id: actor || undefined,
        page_token: pageParam || undefined,
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_page_token ?? undefined,
  });
  const productEvents = useInfiniteQuery({
    queryKey: ["customer-audit-history", orgId],
    queryFn: ({ pageParam }) => portal.auditHistory(orgId, pageParam || undefined),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_page_token ?? undefined,
  });
  // Website and folder names for narrowed roles in before/after snapshots.
  const { names } = useAccessResources(true);

  const people = members.data?.items ?? [];
  const byId = new Map(people.map((m) => [m.user.id, m.user]));
  const nameOf: NameOf = (id) => {
    if (!id) return "CortexGrip";
    if (id === me.id) return "You";
    const u = byId.get(id);
    return u ? shortName(u.display_name, u.email) : "A former member";
  };

  if (events.isPending) return <FullPageSpinner />;
  if (events.isError) return <ErrorState error={events.error} onRetry={() => void events.refetch()} />;
  const rows = events.data.pages.flatMap((p) => p.items);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3.5">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-brand-50 text-fg-muted">
          <ScrollText className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-[26px] font-bold text-fg">Audit log</h1>
          <p className="mt-1 text-sm text-fg-subtle">
            Who changed what in {current.organization.name}: access, roles, billing, domains, hosting and
            settings. Entries can't be edited or deleted. The wider business feed is in{" "}
            <Link className="text-brand-700 underline underline-offset-2 hover:no-underline" to="/activity">
              Activity
            </Link>
            .
          </p>
        </div>
      </div>

      <section className={cx(panel, "px-5 py-4")}>
        <div className="flex flex-wrap items-center gap-2 pb-3">
          <h2 className="mr-auto text-base font-semibold text-fg">Activity</h2>
          <Select
            aria-label="What to show"
            value={category}
            onChange={(e) => setCategory(e.target.value as AuditCategory)}
            className="h-8 w-auto rounded-full py-1 text-[13px]"
          >
            {AUDIT_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Done by"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            className="h-8 w-auto rounded-full py-1 text-[13px]"
          >
            <option value="">Anyone</option>
            {people.map((m) => (
              <option key={m.user.id} value={m.user.id}>
                {m.user.id === me.id ? "You" : shortName(m.user.display_name, m.user.email)}
              </option>
            ))}
          </Select>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            title="Nothing here yet"
            description="Changes will appear here as soon as they're made."
          />
        ) : (
          <ul className="divide-y divide-border border-t border-border">
            {rows.map((e) => (
              <AuditRow key={e.id} event={e} described={describeEvent(e, nameOf)} names={names} />
            ))}
          </ul>
        )}

        {events.hasNextPage && (
          <div className="flex justify-center pt-4">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              loading={events.isFetchingNextPage}
              onClick={() => void events.fetchNextPage()}
            >
              Show older
            </Button>
          </div>
        )}
      </section>

      <section className={cx(panel, "px-5 py-4")}>
        <div className="pb-3">
          <h2 className="text-base font-semibold text-fg">Product activity</h2>
          <p className="mt-1 text-sm text-fg-subtle">
            Billing, domains, hosting, publishing and business settings from CortexGrip services.
          </p>
        </div>
        {productEvents.isPending ? (
          <div className="py-6 text-sm text-fg-muted">Loading product activity…</div>
        ) : productEvents.isError ? (
          <ErrorState error={productEvents.error} onRetry={() => void productEvents.refetch()} />
        ) : (
          <>
            {productEvents.data.pages.flatMap((p) => p.items).length === 0 ? (
              <EmptyState
                title="No product activity yet"
                description="Portal changes will appear here as they happen."
              />
            ) : (
              <ul className="divide-y divide-border border-t border-border">
                {productEvents.data.pages
                  .flatMap((p) => p.items)
                  .map((event) => (
                    <ProductAuditRow key={event.id} event={event} />
                  ))}
              </ul>
            )}
            {productEvents.hasNextPage && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full"
                  loading={productEvents.isFetchingNextPage}
                  onClick={() => void productEvents.fetchNextPage()}
                >
                  Show older
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function ProductAuditRow({ event }: { event: CustomerAuditEvent }) {
  const [open, setOpen] = useState(false);
  const summary = event.action.replaceAll(".", " · ").replaceAll("_", " ");
  return (
    <li className="py-3">
      <Button
        unstyled
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{summary}</span>
            <Badge tone={event.outcome === "success" ? "success" : "danger"}>{event.outcome}</Badge>
          </span>
          <span className="mt-0.5 block text-xs text-fg-muted">
            {event.resource_type} · {event.actor_kind}
          </span>
        </span>
        <span className="shrink-0 text-xs text-fg-subtle" title={formatDateTime(event.occurred_at)}>
          {timeAgo(event.occurred_at)}
        </span>
        <ChevronDown
          aria-hidden
          className={cx("mt-0.5 size-4 shrink-0 text-fg-subtle transition-transform", open && "rotate-180")}
        />
      </Button>
      {open && (
        <div className="mt-3 rounded-xl bg-muted px-4 py-3 text-xs text-fg-muted">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-fg-subtle">When</dt>
            <dd>{formatDateTime(event.occurred_at)}</dd>
            <dt className="text-fg-subtle">Event</dt>
            <dd className="font-mono">{event.action}</dd>
            <dt className="text-fg-subtle">Resource</dt>
            <dd className="font-mono break-all">
              {event.resource_type}/{event.resource_id}
            </dd>
            {event.correlation_id && (
              <>
                <dt className="text-fg-subtle">Reference</dt>
                <dd className="font-mono break-all">{event.correlation_id}</dd>
              </>
            )}
          </dl>
          {(event.before || event.after) && (
            <pre className="mt-3 overflow-auto whitespace-pre-wrap break-words">
              {JSON.stringify({ before: event.before, after: event.after }, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

function AuditRow({
  event: e,
  described: d,
  names,
}: {
  event: AuditEvent;
  described: DescribedEvent;
  names: ResourceNames;
}) {
  const [open, setOpen] = useState(false);
  const hasAccess = d.before !== undefined && d.after !== undefined;
  return (
    <li className="py-3">
      <Button
        unstyled
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{d.summary}</span>
            {d.tone === "danger" && <Badge tone="danger">Refused</Badge>}
          </span>
          {d.reason && <span className="mt-0.5 block text-xs text-fg-muted">{d.reason}</span>}
          {hasAccess && <AccessChangeLine before={d.before!} after={d.after!} names={names} />}
        </span>
        <span className="shrink-0 text-xs text-fg-subtle" title={formatDateTime(e.occurred_at)}>
          {timeAgo(e.occurred_at)}
        </span>
        <ChevronDown
          aria-hidden
          className={cx("mt-0.5 size-4 shrink-0 text-fg-subtle transition-transform", open && "rotate-180")}
        />
      </Button>
      {open && (
        <div className="mt-3 rounded-xl bg-muted px-4 py-3 text-xs text-fg-muted">
          {hasAccess && (
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <Snapshot title="Before" roles={d.before!} names={names} />
              <Snapshot title="After" roles={d.after!} names={names} />
            </div>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-fg-subtle">When</dt>
            <dd>{formatDateTime(e.occurred_at)}</dd>
            <dt className="text-fg-subtle">Event</dt>
            <dd className="font-mono">{e.action}</dd>
            {e.request_id && (
              <>
                <dt className="text-fg-subtle">Reference</dt>
                <dd className="font-mono break-all">{e.request_id}</dd>
              </>
            )}
            {e.ip_address && (
              <>
                <dt className="text-fg-subtle">IP address</dt>
                <dd>{e.ip_address}</dd>
              </>
            )}
            {e.user_agent && (
              <>
                <dt className="text-fg-subtle">Device</dt>
                <dd className="break-all">{e.user_agent}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </li>
  );
}

/** "Website Manager → Domain Manager (Folder: Clients)": the gist of an access change, inline. */
function AccessChangeLine({
  before,
  after,
  names,
}: {
  before: AccessSnapshotRole[];
  after: AccessSnapshotRole[];
  names: ResourceNames;
}) {
  const { added, removed } = roleDiff(before, after);
  if (added.length + removed.length === 0) return null;
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
      {removed.map((r) => (
        <span key={`-${r.role_id}`} className="line-through">
          <RoleLabel role={r} names={names} />
        </span>
      ))}
      {removed.length > 0 && added.length > 0 && <span aria-hidden>→</span>}
      {added.map((r) => (
        <RoleLabel key={`+${r.role_id}`} role={r} names={names} />
      ))}
    </span>
  );
}

function RoleLabel({ role, names }: { role: AccessSnapshotRole; names: ResourceNames }) {
  const scope = restrictionSummary(role.restrictions, names);
  return (
    <span className="inline-flex items-center gap-1">
      <RolePill roleKey={role.role_key}>{role.role_name}</RolePill>
      {scope && <span className="text-[11px] text-fg-subtle">{scope}</span>}
    </span>
  );
}

function Snapshot({
  title,
  roles,
  names,
}: {
  title: string;
  roles: AccessSnapshotRole[];
  names: ResourceNames;
}) {
  return (
    <div>
      <p className="mb-1.5 font-semibold text-fg">{title}</p>
      {roles.length === 0 ? (
        <p>No access</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {roles.map((r) => (
            <li key={r.role_id}>
              <RoleLabel role={r} names={names} />
              {!r.restrictions && <span className="ml-1 text-[11px] text-fg-subtle">whole business</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
